import { useState } from 'react';
import { MessageCircle, Send, X } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  openWhatsApp,
  getAbsenceMessage,
  getPaymentReminderMessage,
  getGeneralMessage,
  getMeetingMessage,
} from '../lib/whatsapp';

const MESSAGE_TEMPLATES = [
  { id: 'absence', label: 'Notification d\'absence' },
  { id: 'payment', label: 'Rappel de paiement' },
  { id: 'meeting', label: 'Convocation' },
  { id: 'custom', label: 'Message personnalisé' },
];

export default function WhatsAppContact({ student, parentType = 'father' }) {
  const [open, setOpen] = useState(false);
  const [template, setTemplate] = useState('custom');
  const [customMessage, setCustomMessage] = useState('');

  // Get parent info based on type
  const getParentInfo = () => {
    const parentInfo = student?.parent_info || {};
    switch (parentType) {
      case 'father':
        return { name: parentInfo.father_name, phone: parentInfo.father_phone };
      case 'mother':
        return { name: parentInfo.mother_name, phone: parentInfo.mother_phone };
      case 'guardian':
        return { name: parentInfo.guardian_name, phone: parentInfo.guardian_phone };
      default:
        return { name: '', phone: '' };
    }
  };

  const parent = getParentInfo();
  const studentName = `${student?.first_name} ${student?.last_name}`;

  const generateMessage = () => {
    const today = new Date().toLocaleDateString('fr-FR');
    
    switch (template) {
      case 'absence':
        return getAbsenceMessage(studentName, today, parent.name || 'Cher parent');
      case 'payment':
        return getPaymentReminderMessage(studentName, '50,000', today, parent.name || 'Cher parent');
      case 'meeting':
        return getMeetingMessage(studentName, today, '10h00', 'Réunion pédagogique', parent.name || 'Cher parent');
      case 'custom':
      default:
        return customMessage || getGeneralMessage(studentName, '', parent.name || 'Cher parent');
    }
  };

  const handleSend = () => {
    if (!parent.phone) {
      alert('Numéro de téléphone non disponible');
      return;
    }
    const message = template === 'custom' ? customMessage : generateMessage();
    openWhatsApp(parent.phone, message);
    setOpen(false);
  };

  if (!parent.phone) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 text-green-600 border-green-200 hover:bg-green-50">
          <MessageCircle className="w-4 h-4" />
          WhatsApp
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-green-600" />
            Contacter via WhatsApp
          </DialogTitle>
          <DialogDescription>
            Envoyer un message à {parent.name || 'Parent'} ({parent.phone})
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Type de message</Label>
            <Select value={template} onValueChange={setTemplate}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MESSAGE_TEMPLATES.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {template === 'custom' ? (
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Écrivez votre message..."
                rows={5}
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Aperçu du message</Label>
              <div className="p-3 bg-muted rounded-lg text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">
                {generateMessage()}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Annuler
          </Button>
          <Button onClick={handleSend} className="bg-green-600 hover:bg-green-700">
            <Send className="w-4 h-4 mr-2" />
            Ouvrir WhatsApp
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
